import React from 'react';
import HeroSection from './HeroSection';
import MetaPartnerSection from './MetaPartnerSection';
import CategoryMarquee from './CategoryMarquee';
import FeatureTabs from './FeatureTabs';
import FeatureShowcase from './FeatureShowcase';
import HowItWorks from './HowItWorks';
import RevampedFooter from './RevampedFooter';

const LandingRevamp = ({ setFeedbackOpen }) => {
  const features = [
    {
      number: 1,
      total: 8,
      title: "Post at the right time",
      subtitle: "More reach. More sales.",
      items: ["Schedule Reels, Stories & posts", "Auto-post on Instagram & Facebook", "Weekly content calendar"],
      badgeText: "Scheduler",
      image: "/assets/revamp/slides/Scheduler.png",
      reverse: false,
    },
    {
      number: 2,
      total: 8,
      title: "Auto-reply to every comment",
      subtitle: "Never miss a buyer again.",
      items: ["Instant replies 24/7", "Understands 'price?', 'details?', 'buy?'", "Moves buyers to DM automatically"],
      badgeText: "Comment Automation",
      image: "/assets/revamp/slides/Comment Automation.png",
      reverse: true,
    },
    {
      number: 3,
      total: 8,
      title: "Sell directly inside DMs",
      subtitle: "No links. No drop-offs.",
      items: ["Swipeable product cards", "Choose size, color, variants", "One-tap ordering"],
      badgeText: "Products",
      image: "/assets/revamp/slides/Products.png",
      reverse: false,
    },
    {
      number: 4,
      total: 8,
      title: "Collect payments instantly",
      subtitle: "Fast. Secure. Simple.",
      items: ["Payment links inside DM", "UPI, cards, wallets supported", "Instant confirmations"],
      badgeText: "Payment Gateway",
      image: "/assets/revamp/slides/Payment Gateway.png",
      reverse: true,
    },
    {
      number: 5,
      total: 8,
      title: "Generate labels in seconds",
      subtitle: "Automate your fulfillment.",
      items: ["Auto-generate shipping labels", "Carrier integration (Bluedart, Delhivery)", "Bulk printing support"],
      badgeText: "Labels",
      image: "/assets/revamp/slides/Shipping Integration.png",
      reverse: false,
    },
    {
      number: 6,
      total: 8,
      title: "Manage orders in one view",
      subtitle: "Never lose an order.",
      items: ["Centralized order dashboard", "Filter by status, customer, date", "One-click order updates"],
      badgeText: "Order Control",
      image: "/assets/revamp/slides/Inventory.png",
      reverse: true,
    },
    {
      number: 7,
      total: 8,
      title: "Smart Shipping updates",
      subtitle: "Keep customers informed.",
      items: ["Auto-tracking notifications", "Real-time delivery status", "Estimated arrival times"],
      badgeText: "Shipping",
      image: "/assets/revamp/slides/Shipping Integration-1.png",
      reverse: false,
    },
    {
      number: 8,
      total: 8,
      title: "WhatsApp Flow automation",
      subtitle: "Connect where they are.",
      items: ["Send order updates on WhatsApp", "Automated customer support", "Rich message templates"],
      badgeText: "WhatsApp",
      image: "/assets/revamp/slides/WhatsApp.png",
      reverse: true,
    }
  ];

  const faqs = [
    {
      question: "How does the comment automation work?",
      answer: "When someone comments on your Reel or post, ReelTale's AI analyzes the intent. If they're asking about price or details, it can automatically reply to the comment and send a DM with a product card and buy link."
    },
    {
      question: "Do I need a website to sell?",
      answer: "No! ReelTale is designed specifically for 'social commerce'. You can sell directly inside Instagram DMs using our swipeable product cards and integrated payment links."
    },
    {
      question: "Which payment methods are supported?",
      answer: "We support UPI (Google Pay, PhonePe, etc.), all major Credit/Debit cards, and popular mobile wallets through our secure payment gateway integration."
    },
    {
      question: "Is my data secure?",
      answer: "Yes. ReelTale is an official Meta Business Partner. We use official Instagram and Facebook APIs and follow industry-standard security protocols to protect your data."
    }
  ];

  return (
    <div className="bg-white dark:bg-gray-900 transition-colors duration-300">
      {/* 1. Hero Section */}
      <HeroSection />

      {/* 2. Meta Partner & Stats */}
      <MetaPartnerSection />

      {/* 3. Category Marquee */}
      <CategoryMarquee />

      {/* 4. Main Feature Navigation */}
      <FeatureTabs />

      {/* 5. Numbered Feature Slides (1-8) */}
      <div className="space-y-0">
        {features.map((feature, index) => (
          <FeatureShowcase key={index} {...feature} />
        ))}
      </div>

      {/* 6. How it Works */}
      <HowItWorks />

      {/* 7. FAQ Section */}
      <section className="py-24 lg:py-40 bg-white dark:bg-gray-900 transition-colors duration-300">
        <div className="container mx-auto px-4">
          <div className="flex flex-col lg:flex-row gap-20 xl:gap-32">
            {/* Left Column: FAQ */}
            <div className="lg:w-3/5">
              <div className="mb-16">
                <h2 className="text-4xl lg:text-5xl font-black text-gray-900 dark:text-white mb-6">Frequently Asked Questions</h2>
                <p className="text-xl text-gray-500 dark:text-gray-400">Everything you need to know about scaling with ReelTale.</p>
              </div>
              <div className="space-y-6">
                {faqs.map((faq, index) => (
                  <details key={index} className="group bg-gray-50 dark:bg-gray-800/40 rounded-[2rem] border border-gray-100 dark:border-gray-700 open:bg-white dark:open:bg-gray-800 open:ring-2 open:ring-purple-500/20 transition-all duration-300">
                    <summary className="flex items-center justify-between p-8 cursor-pointer list-none">
                      <span className="text-xl font-bold text-gray-900 dark:text-white pr-8">{faq.question}</span>
                      <span className="flex-shrink-0 w-10 h-10 rounded-full bg-white dark:bg-gray-700 shadow-sm flex items-center justify-center text-purple-600 transition-transform group-open:rotate-180 group-open:bg-purple-600 group-open:text-white">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M19 9l-7 7-7-7" /></svg>
                      </span>
                    </summary>
                    <div className="px-8 pb-8 text-lg text-gray-600 dark:text-gray-400 leading-relaxed font-medium">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>
            </div>

            {/* Right Column: Contact Us */}
            <div className="lg:w-2/5">
              <div className="bg-gray-900 rounded-[3rem] p-10 lg:p-12 text-white shadow-3xl sticky top-32 relative overflow-hidden group">
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-64 h-64 bg-purple-600/20 rounded-full blur-[80px] -mr-32 -mt-32"></div>
                <div className="absolute bottom-0 left-0 w-64 h-64 bg-indigo-600/20 rounded-full blur-[80px] -ml-32 -mb-32"></div>
                
                <div className="relative z-10">
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-3xl flex items-center justify-center mb-10 shadow-lg shadow-purple-500/30">
                    <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                    </svg>
                  </div>
                  <h3 className="text-3xl font-black mb-6">Still have questions?</h3>
                  <p className="text-gray-400 mb-10 text-xl leading-relaxed">
                    Our team is here to help you automate your business. Send us a message and we'll get back to you shortly.
                  </p>
                  <button 
                    onClick={() => setFeedbackOpen && setFeedbackOpen(true)}
                    className="w-full py-5 bg-white text-gray-900 font-black text-lg rounded-2xl hover:bg-purple-50 transition-all transform hover:scale-[1.02] shadow-xl"
                  >
                    Contact Us Now
                  </button>
                  
                  <div className="mt-12 pt-10 border-t border-gray-800 flex items-center gap-6">
                    <div className="flex -space-x-4">
                      {[1,2,3].map(i => (
                        <div key={i} className="w-12 h-12 rounded-full border-2 border-gray-900 bg-gray-800 flex items-center justify-center overflow-hidden">
                          <img src={`https://i.pravatar.cc/100?img=${i+10}`} alt="Support Team" className="w-full h-full object-cover" />
                        </div>
                      ))}
                    </div>
                    <div className="text-sm">
                      <div className="text-white font-bold">24/7 Support Team</div>
                      <div className="text-gray-500">Average response time: 2h</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* 8. CTA Section */}
      <section className="py-24 lg:py-32 bg-gray-900 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/50 to-transparent pointer-events-none"></div>
        <div className="container mx-auto px-4 relative z-10">
          <h2 className="text-4xl lg:text-6xl font-black text-white mb-8">Ready to grow your business?</h2>
          <p className="text-xl lg:text-2xl text-gray-400 mb-12 max-w-2xl mx-auto">
            Join 1,000+ sellers who are automating their Instagram sales with ReelTale.
          </p>
          <button 
            onClick={() => window.location.href = '/signup'}
            className="px-10 py-5 text-xl font-bold text-white bg-gradient-to-r from-purple-600 to-indigo-600 rounded-full hover:shadow-2xl transition-all transform hover:-translate-y-1"
          >
            Start for free now
          </button>
        </div>
      </section>

      {/* 9. Revamped Footer */}
      <RevampedFooter setFeedbackOpen={setFeedbackOpen} />
    </div>
  );
};

export default LandingRevamp;
